import type { JobView, StepView } from '@/api/opsTypes';
import { formatUntil } from './stepReasons';

/** What went wrong with a step, and what the admin does about it — in words, never a code alone. */
export interface StepAdvice {
  problem: string;
  fix: string;
}

type AdviceStep = Pick<StepView, 'status' | 'error_code' | 'http_status' | 'next_attempt_at'>;

const RETRY_LATER = 'Nothing to do yet — the console tries again on its own';

/** By the codes docs/api-tenant-ops.md §10 lists; `waiting` = the step will be retried automatically. */
function byCode(code: string, waiting: boolean): StepAdvice | null {
  switch (code) {
    // Tenant removal (2026-10-02).
    case 'APPROVAL_TIMEOUT':
      return { problem: 'No other owner approved the deletion in time. Nothing was done to the tenant.', fix: 'Retry to open a new approval window, or cancel the job.' };
    case 'CONFIRMATION_TIMEOUT':
      return {
        problem: 'Nobody confirmed the deletion in time, so the tenant was unfrozen and is online again. Nothing was removed.',
        fix: 'If it should still go: cancel this job and ask for the deletion again from the tenant page.',
      };
    case 'DELETION_REJECTED':
      return { problem: 'An owner stopped the deletion. The tenant is online again.', fix: 'Nothing to do. Ask again from the tenant page if it should go after all.' };
    case 'BACKUP_FAILED':
      return {
        problem: 'The server could not back the tenant up, or the backup did not pass its restore test. Nothing was removed — the tenant stays frozen.',
        fix: 'Check the host runner log on the server (collectors/tasks.log), fix the cause, then retry this step. Or stop the deletion with Reject.',
      };
    case 'HOST_RUNNER_ABSENT':
      return {
        problem: 'Nothing on the server picked the backup up — the host task runner is not installed or not running.',
        fix: 'Install tools/host-tasks.sh on the server (a cron line with a "tasks" import key), then retry this step.',
      };
    case 'BACKUP_TIMEOUT':
      return { problem: 'The backup did not finish within its time.', fix: 'Check the host runner log on the server, then retry this step.' };
    case 'TENANT_IN_USE':
      return {
        problem: 'Someone has signed in to this tenant, so the clean-up kept it.',
        fix: 'If it really should go, delete it from its tenant page (backup and a second owner\'s approval).',
      };
    case 'TENANT_STILL_PRESENT':
      return { problem: 'IAM still reports the tenant.', fix: waiting ? RETRY_LATER : 'Retry this step; if it stays, send the job link to engineering.' };
    case 'BACKUP_NOTHING_TO_DUMP':
      return { problem: 'IAM lists no database for this tenant.', fix: 'Send the job link to engineering before going further.' };
    case 'TENANT_NAME_TAKEN':
      return { problem: 'Another tenant already has this name.', fix: 'Cancel this setup and create the tenant again with a different name.' };
    case 'OWNER_EMAIL_TAKEN':
      return {
        problem: "The owner's email already has an account on this environment, so no tenant was created.",
        fix: 'Cancel this setup and create the tenant again with another owner email.',
      };
    case 'TENANT_DB_EXISTS':
      return {
        problem: 'A tenant database with this short name already exists on this environment.',
        fix: 'Cancel this setup and create the tenant again with a different database name (under Advanced).',
      };
    case 'HOST_TAKEN':
      return {
        problem: 'One of the web addresses is already used by another tenant.',
        fix: 'Free the address on the other tenant, or cancel and create this tenant without it.',
      };
    case 'IDEMPOTENCY_CONFLICT':
      return {
        problem: 'The service already holds a different request for this tenant.',
        fix: 'Do not retry — it will conflict again. Send the job link to engineering: something changed the tenant outside this setup.',
      };
    case 'ENVIRONMENT_KEY_MISSING':
      return { problem: 'The console has no internal key for this environment.', fix: 'Set the key under Platform → Environments, then retry this step.' };
    case 'SERVICE_NOT_CONFIGURED':
      return {
        problem: "The console does not know this service's address on this environment.",
        fix: 'Add the service address under Platform → Environments, then retry this step.',
      };
    case 'ENVIRONMENT_UNAVAILABLE':
    case 'ENVIRONMENT_DISABLED':
      return { problem: 'The environment is switched off or cannot be reached.', fix: 'Switch it on under Platform → Environments, or check its server, then retry.' };
    case 'PLATFORM_UNAVAILABLE':
    case 'HTTP_408':
    case 'HTTP_425':
    case 'HTTP_429':
      return {
        problem: 'The service was down or too slow to answer.',
        fix: waiting ? RETRY_LATER : "Check that the service runs on this environment (the environment's server page), then retry this step.",
      };
    case 'TENANT_NOT_BOOTSTRAPPED':
    case 'TENANT_NOT_PROVISIONED':
    case 'SALESIQ_TENANT_NOT_FOUND':
      return {
        problem: 'The service has not caught up with an earlier step yet.',
        fix: waiting ? RETRY_LATER : 'Retry this step. If it fails again, the service may have missed the new tenant — send the job link to engineering.',
      };
    case 'PARITY_RED':
      return {
        problem: 'A final check found something missing on the tenant.',
        fix: waiting ? RETRY_LATER : 'Open the technical details to see which check failed, retry the step that sets it up, then this check.',
      };
    case 'CREDIT_SCALE_UNAVAILABLE':
    case 'CREDIT_SCALE_UNREADABLE':
      return {
        problem: 'Billing could not say how much a credit is, so the starting credits were not sent (they are never guessed).',
        fix: waiting ? RETRY_LATER : 'Make sure billing runs on this environment, then retry this step.',
      };
    case 'PLAN_NOT_FOUND':
      return { problem: 'The chosen plan does not exist on this environment.', fix: 'Cancel and create the tenant again with an existing plan.' };
    case 'INDUSTRY_PACK_MISSING':
    case 'PACK_SECTION_UNKNOWN':
    case 'PACK_ASSET_MISSING':
      return {
        problem: 'The industry pack this setup used is missing or incomplete in this console version.',
        fix: 'Deploy a console version that ships the pack, then retry this step.',
      };
    case 'KB_NOT_READY_TIMEOUT':
    case 'AGENTS_PACK_REFUSED':
      return { problem: "The pack's AI agents could not be deployed.", fix: 'Retry this step once the knowledge-base documents have finished processing.' };
    case 'PARITY_NOT_FOUND':
      return { problem: 'The service knows nothing about this tenant.', fix: "Retry the service's “Set up” step first, then this check." };
    case 'PARITY_UNREADABLE':
    case 'IAM_RESPONSE_UNREADABLE':
    case 'PLANS_UNREADABLE':
    case 'SETTINGS_UNREADABLE':
      return {
        problem: "The service's answer could not be read.",
        fix: waiting ? RETRY_LATER : 'Retry this step. If it repeats, send the job link to engineering.',
      };
    case 'SETTINGS_VERSION_CONFLICT':
      return { problem: 'Settings changed while this step was writing them.', fix: waiting ? RETRY_LATER : 'Retry this step.' };
    case 'SETTINGS_LEGACY_TABLE_NOT_EMPTY':
      return { problem: 'Old settings data on this tenant must be migrated first.', fix: 'An engineer must migrate it — do not retry.' };
    case 'TENANT_ID_MISMATCH':
      return { problem: 'IAM answered for a different tenant than this setup.', fix: 'Stop here and send the job link to engineering.' };
    case 'PLAN_GRANT_OUT_OF_RANGE':
      return { problem: "The plan's starting credits are outside the allowed range.", fix: 'An engineer must fix the plan, then retry this step.' };
    case 'GRANT_TARGET_UNKNOWN':
    case 'STEP_KIND_UNKNOWN':
    case 'SETTINGS_DESCRIPTOR_INVALID':
      return { problem: 'The setup steps are misconfigured for this environment.', fix: 'An engineer must fix the step configuration, then retry.' };
    case 'RUNNER_EXCEPTION':
      return {
        problem: 'The console hit an unexpected error.',
        fix: waiting ? RETRY_LATER : 'Retry this step. If it happens again, send the job link to engineering.',
      };
    default:
      return null;
  }
}

function byHttp(status: number | null): StepAdvice {
  if (status === 401 || status === 403) {
    return { problem: "The service refused the console's key.", fix: "Check the environment's internal key under Platform → Environments, then retry this step." };
  }
  if (status === 404) {
    return { problem: 'The service does not know this request — it may be older than the console.', fix: 'Update the service on this environment, then retry this step.' };
  }
  if (status === 409) {
    return { problem: 'The service reports a conflict with data that already exists.', fix: 'Read the message below, resolve the conflict, then retry this step.' };
  }
  if (status !== null && status >= 400 && status < 500) {
    return { problem: 'The service refused the request.', fix: 'Read the message below, fix the cause, then retry this step.' };
  }
  return { problem: 'The step failed.', fix: 'Retry this step. If it fails again, send the job link to engineering.' };
}

/** Advice for a failed step, or a step waiting for its automatic retry; null for every other status. */
export function stepAdvice(step: AdviceStep, now: number = Date.now()): StepAdvice | null {
  const waiting = step.status === 'waiting_retry';
  if (step.status !== 'failed' && !waiting) return null;

  const advice = (step.error_code ? byCode(step.error_code, waiting) : null) ?? byHttp(step.http_status);
  if (waiting && step.next_attempt_at) {
    return { ...advice, fix: advice.fix === RETRY_LATER ? `${RETRY_LATER} (${formatUntil(step.next_attempt_at, now)}).` : advice.fix };
  }
  return waiting && advice.fix === RETRY_LATER ? { ...advice, fix: `${RETRY_LATER}.` } : advice;
}

/** A job's status in words. */
export function jobStatusText(status: JobView['status'], op?: string): string {
  switch (status) {
    case 'pending': return 'Waiting to start';
    case 'running': return op === 'delete' || op === 'discard' ? 'Removing…' : 'Setting up…';
    case 'failed': return 'Stopped — needs you';
    case 'done': return op === 'delete' || op === 'discard' ? 'Removed' : 'Ready';
    case 'done_with_warnings': return op === 'delete' || op === 'discard' ? 'Removed, with warnings' : 'Ready, with warnings';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
}
